import { computed, ref, type ComputedRef, type InjectionKey, type Ref } from 'vue';

export interface DocumentSelectionFilters {
  hasTraining?: boolean | null;
  startDate?: Date | null;
  endDate?: Date | null;
  revision?: number | null;
}

export interface DocumentSelectionFilterContext {
  activeFilters: Ref<DocumentSelectionFilters>;
  isFilterActive: ComputedRef<boolean>;
  isFilterPanelOpen: Ref<boolean>;
  toggleFilterPanel: () => void;
  closeFilterPanel: () => void;
  applyFilters: (filters: DocumentSelectionFilters) => void;
}

export const DocumentSelectionFilterInjectionKey: InjectionKey<DocumentSelectionFilterContext> =
  Symbol('document-selection-filter');

export function hasActiveDocumentSelectionFilters(filters: DocumentSelectionFilters): boolean {
  return (
    (filters.hasTraining !== null && filters.hasTraining !== undefined) ||
    Boolean(filters.startDate) ||
    Boolean(filters.endDate) ||
    Boolean(filters.revision && filters.revision > 0)
  );
}

function filtersKey(filters: DocumentSelectionFilters): string {
  const hasTraining = filters.hasTraining ?? '';
  const startDate = filters.startDate ? filters.startDate.getTime() : '';
  const endDate = filters.endDate ? filters.endDate.getTime() : '';
  const revision = filters.revision && filters.revision > 0 ? filters.revision : '';
  return `${hasTraining}|${startDate}|${endDate}|${revision}`;
}

export function createDocumentSelectionFilterContext(
  onApply?: (filters: DocumentSelectionFilters) => void
): DocumentSelectionFilterContext {
  const activeFilters = ref<DocumentSelectionFilters>({});
  const isFilterPanelOpen = ref(false);
  const isFilterActive = computed(() => hasActiveDocumentSelectionFilters(activeFilters.value));

  function toggleFilterPanel(): void {
    isFilterPanelOpen.value = !isFilterPanelOpen.value;
  }

  function closeFilterPanel(): void {
    isFilterPanelOpen.value = false;
  }

  function applyFilters(filters: DocumentSelectionFilters): void {
    const changed = filtersKey(filters) !== filtersKey(activeFilters.value);
    activeFilters.value = filters;
    isFilterPanelOpen.value = false;

    if (changed) {
      onApply?.(filters);
    }
  }

  return {
    activeFilters,
    isFilterActive,
    isFilterPanelOpen,
    toggleFilterPanel,
    closeFilterPanel,
    applyFilters,
  };
}
